import { useEffect, useRef, useState } from "react";
import { wsUrl } from "../lib/api";

const EMOJIS = ["👍", "👎", "❤️", "🔥"];

/**
 * Live reaction layer.
 *
 * - Renders a compact bottom-right dock of 4 emoji buttons (👍 👎 ❤️ 🔥).
 * - On tap, sends `{type: "reaction", emoji, member_no}` over the same
 *   /ws/chat/{live_id} WebSocket the chat panel uses (a dedicated one; we
 *   don't share the chat instance to keep concerns clean).
 * - Renders inbound `reaction` broadcasts as floating emojis that drift up
 *   and fade, TikTok-live style. Tap-spam friendly: every tap = one glyph.
 * - Works identically on the member LiveRoom and the host console.
 *
 * Emojis are absolute-positioned inside a fixed container so they don't
 * affect layout; container is pointer-events-none, buttons are the only
 * interactive surface.
 */
export default function LiveReactions({ liveId, memberNo, position = "bottom-right", compact = false, hostLabel = false, bottomPx = 88 }) {
  const wsRef = useRef(null);
  const [floaters, setFloaters] = useState([]);
  const floaterIdRef = useRef(0);

  useEffect(() => {
    if (!liveId) return undefined;
    // We open a *separate* WS so we don't fight the ChatPanel over onmessage.
    // The backend accepts multiple sockets per room; each one is broadcast to.
    const ws = new WebSocket(wsUrl(`/api/ws/chat/${liveId}`));
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch (_e) { return; }
      if (msg?.type === "reaction" && msg.emoji) {
        spawnFloater(msg.emoji);
      }
    };
    return () => {
      try { ws.close(); } catch (_e) { /* noop */ }
      wsRef.current = null;
    };
  }, [liveId]);

  const spawnFloater = (emoji) => {
    const id = ++floaterIdRef.current;
    // Random horizontal offset within the launch strip (0-80px) and slight
    // drift target so glyphs don't all rise in a single stack.
    const startX = Math.random() * 80 - 40;
    const driftX = (Math.random() - 0.5) * 60;
    setFloaters((prev) => {
      // Cap concurrent floaters to avoid runaway DOM on tap-spam storms.
      const next = [...prev, { id, emoji, startX, driftX }];
      return next.length > 40 ? next.slice(-40) : next;
    });
    setTimeout(() => {
      setFloaters((prev) => prev.filter((f) => f.id !== id));
    }, 2400);
  };

  const send = (emoji) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      // Fallback: at least render the floater locally so the tapper gets feedback.
      spawnFloater(emoji);
      return;
    }
    try {
      ws.send(JSON.stringify({ type: "reaction", emoji, member_no: memberNo || 0 }));
    } catch (_e) {
      spawnFloater(emoji);
    }
  };

  const posClass = position === "bottom-left" ? "left-3" : "right-3";
  const btnCls = compact
    ? "w-9 h-9 text-lg"
    : "w-11 h-11 text-xl";

  return (
    <div
      className={`pointer-events-none fixed z-30 ${posClass}`}
      style={{ bottom: `calc(env(safe-area-inset-bottom) + ${bottomPx}px)` }}
      data-testid="live-reactions"
    >
      {/* Floater launch column — glyphs animate up from the top of the button dock */}
      <div className="relative w-14 h-64 pointer-events-none" aria-hidden="true">
        {floaters.map((f) => (
          <span
            key={f.id}
            className="absolute bottom-0 left-1/2 text-3xl select-none animate-reaction-float"
            style={{
              transform: `translateX(calc(-50% + ${f.startX}px))`,
              "--drift-x": `${f.driftX}px`,
            }}
          >
            {f.emoji}
          </span>
        ))}
      </div>

      {/* Button dock */}
      <div className="pointer-events-auto flex flex-col gap-1.5 items-center">
        {hostLabel && (
          <span className="font-mono-ds text-[9px] uppercase tracking-[0.24em] text-white/60 mb-0.5" data-testid="reactions-host-label">
            Audience
          </span>
        )}
        {EMOJIS.map((e) => (
          <button
            key={e}
            onClick={() => send(e)}
            className={`${btnCls} rounded-full border border-white/20 bg-black/50 backdrop-blur hover:bg-black/70 active:scale-90 transition-transform leading-none flex items-center justify-center shadow-[0_2px_10px_rgba(0,0,0,0.3)]`}
            data-testid={`reaction-btn-${e}`}
            aria-label={`React ${e}`}
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}
